import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { useContent } from '../contexts/ContentContext';
import AddressModal from './AddressModal';
import StepNavigation from './StepNavigation';
import { findProductConfig, getProductRatio } from '../utils/productUtils';

function CheckoutSection({ items, printSizes, shippingOptions, onOrderComplete, setActiveTab, uploadedImages, user, onUserUpdate }) {
  const { t } = useContent();
  const [cartTotal, setCartTotal] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState(null); // Start with no selection
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  
  // Global attribute selection for entire cart - persist in localStorage
  const [globalPaperType, setGlobalPaperType] = useState(() => {
    return localStorage.getItem('checkoutPaperType') || null;
  });
  const [globalBorderOption, setGlobalBorderOption] = useState(() => {
    return localStorage.getItem('checkoutBorderOption') || null;
  });
  const [availablePaperTypes, setAvailablePaperTypes] = useState([]);
  const [availableBorderOptions, setAvailableBorderOptions] = useState([]);
  
  // Save selections to localStorage whenever they change
  useEffect(() => {
    if (globalPaperType) {
      localStorage.setItem('checkoutPaperType', globalPaperType);
    }
  }, [globalPaperType]);
  
  useEffect(() => {
    if (globalBorderOption) {
      localStorage.setItem('checkoutBorderOption', globalBorderOption);
    }
  }, [globalBorderOption]);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  
  // Minimum order amount and payment settings
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(0);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [squareAppId, setSquareAppId] = useState('');
  
  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    email: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'AU'
  });
  
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [requiresAddress, setRequiresAddress] = useState(true);

  // Load site settings (minimum order, payment providers)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get('/settings');
        const settings = response.data;
        setMinimumOrderAmount(settings.minimum_order_amount || 0);
        setPaymentProviders(settings.payment_providers || []);
        setTestModeEnabled(settings.test_mode_enabled || false);
        setStripePublicKey(settings.stripe_public_key || '');
        setSquareAppId(settings.square_app_id || '');
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  // Load user profile and auto-populate shipping address
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const response = await axios.get('/user/profile');
        const profile = response.data;
        
        // Auto-populate from user profile if available
        if (profile) {
          setShippingAddress({
            name: profile.name || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address_line_1: profile.address_line_1 || '',
            address_line_2: profile.address_line_2 || '',
            city: profile.city || '',
            state: profile.state || '',
            postal_code: profile.postal_code || '',
            country: profile.country || 'AU'
          });
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    
    loadUserProfile();
  }, [user]); // Reload when user changes

  useEffect(() => {
    if (items.length > 0) {
      calculateCartTotal();
      loadProductAttributes();
    } else {
      setCartTotal(null);
    }
  }, [items]);
  
  // Check if selected shipping requires address
  useEffect(() => {
    if (!selectedShipping) {
      setRequiresAddress(false); // Hide address section if no shipping selected
      return;
    }
    
    const selectedOption = shippingOptions[selectedShipping];
    if (selectedOption) {
      const addressRequired = selectedOption.requires_address !== false;
      setRequiresAddress(addressRequired);
    }
  }, [selectedShipping, shippingOptions]);
  
  // Recalculate cart total when global attributes change
  useEffect(() => {
    if (items.length > 0 && (globalPaperType || globalBorderOption)) {
      calculateCartTotal();
    }
  }, [globalPaperType, globalBorderOption]);
  
  // Load product attributes for cart items - sets global options for all items
  const loadProductAttributes = async () => {
    try {
      // Fetch full attribute details with descriptions
      const attributesResponse = await axios.get('/attributes');
      const allAttributeTypes = attributesResponse.data;
      
      // Extract paper types and border options with full details
      const paperTypesData = allAttributeTypes.find(at => at.type.id === 'paper_types');
      const borderOptionsData = allAttributeTypes.find(at => at.type.id === 'border_options');
      
      const fullPaperTypes = paperTypesData ? paperTypesData.attributes : [];
      const fullBorderOptions = borderOptionsData ? borderOptionsData.attributes : [];
      
      // Fetch product info to see which attributes are assigned
      const productsResponse = await axios.get('/print/sizes');
      const allProducts = productsResponse.data;
      
      console.log('🔍 CheckoutSection: Loading product attributes');
      console.log('   All products keys:', Object.keys(allProducts));
      
      if (items.length > 0) {
        const firstItem = items[0];
        console.log('   First cart item:', { sku: firstItem.sku, size: firstItem.size });
        
        // Use SKU for lookup, fall back to size for backward compatibility
        // Also search by size property in case products are keyed by SKU but cart has old size
        let itemKey = firstItem.sku || firstItem.size;
        let product = allProducts[itemKey];
        
        // If not found by key, search through products by size property
        if (!product) {
          const productEntry = Object.entries(allProducts).find(([key, p]) => 
            p.size === firstItem.size || p.sku === firstItem.size
          );
          if (productEntry) {
            product = productEntry[1];
            console.log('   Found product by size search:', product.sku);
          }
        }
        
        console.log('   Found product:', product ? product.sku : 'NOT FOUND');
        
        if (product) {
          // Get assigned attribute IDs from product
          const assignedPaperTypeIds = (product.paper_types || []).map(pt => typeof pt === 'string' ? pt : pt.id);
          const assignedBorderOptionIds = (product.border_options || []).map(bo => typeof bo === 'string' ? bo : bo.id);
          
          console.log('   Assigned paper types:', assignedPaperTypeIds);
          console.log('   Assigned border options:', assignedBorderOptionIds);
          
          // Filter full attributes to only show assigned ones (with full details including description)
          const paperTypes = assignedPaperTypeIds.length > 0 
            ? fullPaperTypes.filter(pt => assignedPaperTypeIds.includes(pt.id))
            : fullPaperTypes; // If no specific assignment, show all
          
          const borderOptions = assignedBorderOptionIds.length > 0
            ? fullBorderOptions.filter(bo => assignedBorderOptionIds.includes(bo.id))
            : fullBorderOptions; // If no specific assignment, show all
          
          setAvailablePaperTypes(paperTypes);
          setAvailableBorderOptions(borderOptions);
          
          if (!globalPaperType && paperTypes.length > 0) {
            const defaultPaper = paperTypes[0]?.id || paperTypes[0] || 'M';
            setGlobalPaperType(defaultPaper);
          }
          
          if (!globalBorderOption && borderOptions.length > 0) {
            const defaultBorder = borderOptions[0]?.id || borderOptions[0] || 'NB';
            setGlobalBorderOption(defaultBorder);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load product attributes:', error);
    }
  };

  const enrichCartItemsWithImageData = (cartItems) => {
    return cartItems.map(item => {
      const uploadedImage = uploadedImages.find(img => img.filename === item.filename);
      
      // Get the ratio from printSizes configuration using shared utility
      const { config: sizeConfig, key: productKey } = findProductConfig(item, printSizes);
      const ratio = getProductRatio(sizeConfig);
      
      // Preserve aspectOrientation from cart item (critical for correct thumbnail display)
      const aspectOrientation = item.aspectOrientation || 'landscape';
      
      console.log(`📦 Checkout enrich: ${item.filename} - sku=${item.sku}, key=${productKey}, ratio=${ratio}, orientation=${aspectOrientation}`);
      
      if (uploadedImage) {
        return {
          ...item,
          sku: item.sku || productKey,  // Ensure SKU is set
          image_data: uploadedImage.image_data,
          image_id: uploadedImage.id,
          thumbnail: uploadedImage.thumbnail,
          ratio: ratio, // Include ratio for order display
          aspectOrientation: aspectOrientation // Preserve orientation
        };
      }
      
      return {
        ...item,
        sku: item.sku || productKey,  // Ensure SKU is set
        ratio: ratio, // Include ratio even without uploadedImage
        aspectOrientation: aspectOrientation // Preserve orientation
      };
    });
  };

  const calculateCartTotal = async () => {
    setCartTotal(null);
    
    try {
      const enrichedItems = enrichCartItemsWithImageData(items).map(item => ({
        ...item,
        paper_type: globalPaperType || 'L',
        border: globalBorderOption ? (globalBorderOption !== 'N' && globalBorderOption !== 'NB') : false
      }));
      
      const response = await axios.post('/cart/calculate', enrichedItems);
      setCartTotal(response.data);
    } catch (error) {
      console.error('Failed to calculate cart total:', error);
      setCartTotal(null);
    }
  };
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      // Use subtotal (items total) for minimum order validation
      let subtotal = cartTotal?.subtotal || 0;
      
      if (subtotal === 0 && items.length > 0) {
        subtotal = items.reduce((sum, item) => {
          const itemKey = item.sku || item.size;
          const product = printSizes[itemKey];
          return sum + ((product?.price || 0) * item.quantity);
        }, 0);
      }
      
      const response = await axios.post('/cart/validate-coupon', {
        code: couponCode,
        cart_total: subtotal
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`✅ Coupon applied! ${response.data.discount_type === 'percentage' ? response.data.discount_value + '%' : '$' + (response.data.discount_value / 100).toFixed(2)} off`);
      } else {
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Failed to apply coupon:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to apply coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };
  
  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    
    const subtotal = cartTotal?.subtotal || 0;
    
    if (appliedCoupon.discount_type === 'percentage') {
      return Math.floor(subtotal * (appliedCoupon.discount_value / 100));
    } else {
      return appliedCoupon.discount_value;
    }
  };

  const validateShippingAddress = () => {
    // Only validate if shipping requires address
    if (!requiresAddress) {
      return true;
    }
    
    // Check if address is provided
    if (!shippingAddress.name || !shippingAddress.email || !shippingAddress.address_line_1) {
      toast.error('Please add a delivery address');
      setShowAddressModal(true);
      return false;
    }
    
    return true;
  };

  const handleSelectPaymentMethod = (method) => {
    // Validate shipping address before proceeding
    if (!validateShippingAddress()) {
      return;
    }
    
    setSelectedPaymentMethod(method);
    setShowPaymentModal(false);
    
    if (method === 'stripe') {
      handleStripeCheckout();
    } else if (method === 'square') {
      handleSquareCheckout();
    } else if (method === 'test_mode') {
      handleTestModeCheckout();
    }
  };
  
  const handleTestModeCheckout = async () => {
    if (!cartTotal || items.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    setLoading(true);
    try {
      // Enrich cart items with image data and ensure all required fields
      const enrichedItems = items.map(item => {
        // Try to find the uploaded image by filename or id
        const uploadedImage = uploadedImages.find(img => 
          img.filename === item.filename || 
          img.id === item.id || 
          img.id === item.image_id
        );
        
        // Get thumbnail - PRIORITY: cropped thumbnail > item thumbnail > original
        // Look for cropped thumbnail using SKU and orientation
        let thumbnail = null;
        const sku = item.sku || item.size;
        const orientation = item.aspectOrientation || 'landscape';
        
        if (uploadedImage?.croppedThumbnails) {
          // Try SKU-orientation key first (e.g., "P64-portrait")
          const skuOrientationKey = `${sku}-${orientation}`;
          if (uploadedImage.croppedThumbnails[skuOrientationKey]?.data) {
            thumbnail = uploadedImage.croppedThumbnails[skuOrientationKey].data;
            console.log(`✅ Using cropped thumbnail for ${item.filename} - ${skuOrientationKey}`);
          }
          // Try plain SKU key
          else if (uploadedImage.croppedThumbnails[sku]?.data) {
            thumbnail = uploadedImage.croppedThumbnails[sku].data;
            console.log(`✅ Using cropped thumbnail for ${item.filename} - ${sku}`);
          }
        }
        
        // Fall back to original thumbnail if no cropped version exists
        if (!thumbnail) {
          thumbnail = item.thumbnail || uploadedImage?.thumbnail || item.image_data;
          console.log(`⚠️ Using original thumbnail for ${item.filename} (no cropped version found)`);
        }
        
        return {
          ...item,
          paper_type: item.paper_type || globalPaperType || 'M',
          border: item.border !== undefined ? item.border : (globalBorderOption ? (globalBorderOption !== 'N' && globalBorderOption !== 'NB') : false),
          image_id: uploadedImage?.id || item.image_id || item.id,
          thumbnail: thumbnail,
          image_data: undefined // Don't send image_data, use image_id instead
        };
      });
      
      // Calculate totals
      const couponDiscount = calculateDiscount();
      const subtotalAfterDiscount = cartTotal.subtotal_after_discount - couponDiscount;
      const shipping = shippingOptions[selectedShipping]?.cost || 0;
      let totalAmount = subtotalAfterDiscount + shipping;
      
      if (!cartTotal.tax_included_in_prices) {
        let taxAmount = cartTotal.tax_amount;
        if (cartTotal.tax_apply_to_coupons && couponDiscount > 0) {
          const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
          taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate);
        }
        if (cartTotal.tax_included_in_shipping && shipping > 0) {
          taxAmount += Math.floor(shipping * cartTotal.tax_rate);
        }
        totalAmount += taxAmount;
      }
      
      // Create order with test payment
      const orderPayload = {
        user_id: user?.id || 'guest',
        items: enrichedItems,
        total_amount: totalAmount,
        subtotal: cartTotal.subtotal,
        shipping_cost: shipping,
        shipping_method: shippingOptions[selectedShipping],
        tax_amount: cartTotal.tax_amount,
        discount_amount: cartTotal.discount_amount + couponDiscount,
        payment_method: 'test_mode',
        payment_status: 'paid',  // Mark as paid immediately
        coupon_code: appliedCoupon?.code || null,
        shipping_address: shippingAddress,
        global_paper_type: globalPaperType,
        global_border_option: globalBorderOption
      };
      
      const response = await axios.post('/orders/create', orderPayload);
      toast.success('✅ Test order created successfully (marked as paid)');
      
      // Clear cart and redirect
      if (onOrderComplete) {
        onOrderComplete(response.data);
      }
    } catch (error) {
      console.error('Test checkout failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create test order: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStripeCheckout = async () => {
    if (!cartTotal || items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    if (!globalPaperType || !globalBorderOption) {
      toast.error('Please select paper type and border options for your order');
      return;
    }

    setLoading(true);
    try {
      const enrichedItems = enrichCartItemsWithImageData(items).map(item => ({
        ...item,
        paper_type: globalPaperType,
        border: globalBorderOption !== 'N' && globalBorderOption !== 'NB'
      }));
      
      // Calculate totals for order
      const couponDiscount = calculateDiscount();
      const shipping = shippingOptions[selectedShipping]?.cost || 0;
      
      // Calculate tax
      let taxAmount = 0;
      if (cartTotal.tax_amount > 0) {
        taxAmount = cartTotal.tax_amount;
        
        // Recalculate tax if coupon is applied and affects tax
        if (cartTotal.tax_apply_to_coupons && couponDiscount > 0) {
          if (cartTotal.tax_included_in_prices) {
            const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
            taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate / (1 + cartTotal.tax_rate));
          } else {
            const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
            taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate);
          }
        }
        
        // Add tax on shipping if enabled
        if (cartTotal.tax_included_in_shipping && shipping > 0) {
          taxAmount += Math.floor(shipping * cartTotal.tax_rate);
        }
      }
      
      const orderData = {
        items: enrichedItems,
        coupon_code: appliedCoupon?.code,
        discount_amount: couponDiscount, // Send actual coupon discount
        tax_amount: taxAmount, // Send calculated tax
        tax_included_in_prices: cartTotal.tax_included_in_prices, // Tell backend if tax is included
        shipping: {
          method: selectedShipping,
          cost: shipping, // Send actual shipping cost
          address: shippingAddress
        }
      };

      const response = await axios.post('/orders/create', orderData);
      
      // Save shipping address to user profile for future use
      try {
        await axios.put('/user/profile', shippingAddress);
      } catch (err) {
        console.error('Failed to save shipping address to profile:', err);
      }
      
      setCurrentOrder(response.data);
      setShowPayment(true);
      toast.success('Order created! Processing Stripe payment...');
      
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSquareCheckout = async () => {
    if (!cartTotal || items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    if (!globalPaperType || !globalBorderOption) {
      toast.error('Please select paper type and border options for your order');
      return;
    }

    setLoading(true);
    try {
      const enrichedItems = enrichCartItemsWithImageData(items).map(item => ({
        ...item,
        paper_type: globalPaperType,
        border: globalBorderOption !== 'N' && globalBorderOption !== 'NB'
      }));
      
      // Calculate totals for order
      const couponDiscount = calculateDiscount();
      const shipping = shippingOptions[selectedShipping]?.cost || 0;
      
      // Calculate tax
      let taxAmount = 0;
      if (cartTotal.tax_amount > 0) {
        taxAmount = cartTotal.tax_amount;
        
        // Recalculate tax if coupon is applied and affects tax
        if (cartTotal.tax_apply_to_coupons && couponDiscount > 0) {
          if (cartTotal.tax_included_in_prices) {
            const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
            taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate / (1 + cartTotal.tax_rate));
          } else {
            const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
            taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate);
          }
        }
        
        // Add tax on shipping if enabled
        if (cartTotal.tax_included_in_shipping && shipping > 0) {
          taxAmount += Math.floor(shipping * cartTotal.tax_rate);
        }
      }
      
      const orderData = {
        items: enrichedItems,
        coupon_code: appliedCoupon?.code,
        discount_amount: couponDiscount, // Send actual coupon discount
        tax_amount: taxAmount, // Send calculated tax
        tax_included_in_prices: cartTotal.tax_included_in_prices, // Tell backend if tax is included
        shipping: {
          method: selectedShipping,
          cost: shipping, // Send actual shipping cost
          address: shippingAddress
        }
      };

      // Create order
      const orderResponse = await axios.post('/orders/create', orderData);
      const order = orderResponse.data;
      
      // Create Square payment session
      const paymentResponse = await axios.post('/payment/create-session', {
        order_id: order.order_id,
        payment_method: 'square',
        return_url: window.location.origin + '/payment/success'
      });
      
      // Redirect to Square checkout
      if (paymentResponse.data.checkout_url) {
        toast.success('Redirecting to Square checkout...');
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        toast.error('Failed to initialize Square payment');
      }
      
    } catch (error) {
      console.error('Square checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Square checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = () => {
    setShowPayment(false);
    setCurrentOrder(null);
    onOrderComplete();
    toast.success('Payment completed successfully! Redirecting to your orders...');
    setTimeout(() => {
      setActiveTab('orders');
    }, 1500);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No items in checkout</h3>
        <p className="text-gray-600 mb-4">Please add items to your cart first</p>
        <Button onClick={() => setActiveTab('cart')}>
          Return to Cart
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step Navigation */}
      <StepNavigation 
        currentStep="checkout"
        onStepChange={setActiveTab}
        canGoNext={true}
        canGoBack={true}
      />
      
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Checkout</h2>
        <p className="text-gray-600">Review your order and complete payment</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN: Discount Code + Payment Options */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount Code</h3>
            
            {/* Coupon Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Discount Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 px-3 py-2 border rounded-md"
                  disabled={appliedCoupon !== null}
                />
                {!appliedCoupon ? (
                  <Button 
                    variant="outline"
                    onClick={handleApplyCoupon} 
                    disabled={applyingCoupon}
                    className="text-custom-orange hover:text-orange-700 hover:bg-orange-50"
                  >
                    {applyingCoupon ? 'Applying...' : 'Apply'}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => { setAppliedCoupon(null); setCouponCode(''); }}
                    variant="outline"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment & Shipping Options</h3>
            
            {/* Shipping Method */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Method <span className="text-red-500">*</span></label>
              <div className="space-y-3">
                {Object.entries(shippingOptions).map(([key, option]) => (
                  <label key={key} className="animated-border-element flex items-start cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="shipping-method"
                      value={key}
                      checked={selectedShipping === key}
                      onChange={(e) => setSelectedShipping(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.name}</div>
                      <div className="text-sm text-gray-600">
                        ${(option.cost / 100).toFixed(2)} - {option.days}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {!selectedShipping && (
                <p className="text-sm text-red-500 mt-2">Please select a shipping method</p>
              )}
            </div>
            
            {/* Delivery Address - Only show if selected shipping requires address */}
            {requiresAddress && (
              <div className="mb-6 pb-6 border-b">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-gray-900">Delivery Address</h4>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddressModal(true)}
                    className="text-sm"
                    size="sm"
                  >
                    {shippingAddress.name ? 'Edit Address' : 'Add Address'}
                  </Button>
                </div>
                
                {shippingAddress.name ? (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="font-medium text-gray-900">{shippingAddress.name}</p>
                    {shippingAddress.phone && <p className="text-sm text-gray-600">{shippingAddress.phone}</p>}
                    <p className="text-sm text-gray-600">{shippingAddress.email}</p>
                    <p className="text-sm text-gray-600 mt-2">
                      {shippingAddress.address_line_1}
                      {shippingAddress.address_line_2 && `, ${shippingAddress.address_line_2}`}
                    </p>
                    <p className="text-sm text-gray-600">
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                    </p>
                    <p className="text-sm text-gray-600">{shippingAddress.country}</p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                    <p className="text-sm text-yellow-800">
                      Please add a delivery address to continue with checkout.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>
          
          {/* Address Modal */}
          <AddressModal
            open={showAddressModal}
            onClose={async () => {
              setShowAddressModal(false);
              // Refresh user profile after modal close
              if (onUserUpdate) {
                try {
                  const response = await axios.get('/user/profile');
                  onUserUpdate(response.data);
                } catch (error) {
                  console.error('Failed to refresh user profile:', error);
                }
              }
            }}
            onSave={async (address) => {
              // Update local state
              setShippingAddress(address);
              // Refresh user profile to sync
              if (onUserUpdate) {
                try {
                  const response = await axios.get('/user/profile');
                  onUserUpdate(response.data);
                } catch (error) {
                  console.error('Failed to refresh user profile:', error);
                }
              }
            }}
            initialAddress={shippingAddress.name ? shippingAddress : null}
            title="Delivery Address"
            addressType="shipping"
            saveToProfile={true}
          />
        </div>
        
        {/* RIGHT COLUMN: Order Summary */}
        <Card className="p-6 lg:sticky lg:top-4 h-fit">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          
          {/* Global Attribute Selection */}
          {items.length > 0 && (
            <div className="mb-6 pb-6 border-b space-y-4">
              {/* Paper Type */}
              {availablePaperTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paper Type <span className="text-gray-500 text-xs">(applies to all items)</span>
                  </label>
                  <div className="space-y-2">
                    {availablePaperTypes.map((paperType) => {
                      const ptId = typeof paperType === 'string' ? paperType : paperType.id;
                      const ptName = typeof paperType === 'string' ? paperType : paperType.name;
                      const ptDescription = typeof paperType === 'string' ? '' : (paperType.description || '');
                      const ptPrice = typeof paperType === 'object' ? (paperType.price || 0) : 0;
                      const isSelected = globalPaperType === ptId;
                      
                      return (
                        <div key={ptId}>
                          <label 
                            className={`animated-border-element flex items-center justify-between cursor-pointer p-3 rounded-lg border-2 transition-all ${
                              isSelected 
                                ? 'border-custom-orange bg-orange-50' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center flex-1">
                              <div className="relative flex items-center">
                                <input
                                  type="radio"
                                  name="global-paper-type"
                                  value={ptId}
                                  checked={isSelected}
                                  onChange={(e) => setGlobalPaperType(e.target.value)}
                                  className="sr-only"
                                />
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? 'border-custom-orange bg-custom-orange' 
                                    : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 rounded-full bg-white"></div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-3">
                                <span className={`text-sm ${isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {ptName}
                                </span>
                                {ptDescription && (
                                  <p className="text-xs text-gray-500 mt-0.5">{ptDescription}</p>
                                )}
                              </div>
                            </div>
                            {(ptPrice > 0 || (paperType.percentage && paperType.percentage !== 0)) && (
                              <span className={`text-sm font-semibold ml-3 ${isSelected ? 'text-custom-orange' : 'text-gray-600'}`}>
                                {paperType.price_type === 'percentage' ? 
                                  `${paperType.percentage > 0 ? '+' : ''}${paperType.percentage}%` :
                                  `${ptPrice > 0 ? '+' : ''}$${Math.abs(ptPrice / 100).toFixed(2)}`
                                }
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Border Options */}
              {availableBorderOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Border <span className="text-gray-500 text-xs">(applies to all items)</span>
                  </label>
                  <div className="space-y-2">
                    {availableBorderOptions.map((borderOption) => {
                      const boId = typeof borderOption === 'string' ? borderOption : borderOption.id;
                      const boName = typeof borderOption === 'string' ? borderOption : borderOption.name;
                      const boDescription = typeof borderOption === 'string' ? '' : borderOption.description;
                      const boPrice = typeof borderOption === 'object' ? (borderOption.price || 0) : 0;
                      const isSelected = globalBorderOption === boId;
                      
                      return (
                        <div key={boId}>
                          <label 
                            className={`animated-border-element flex items-center justify-between cursor-pointer p-3 rounded-lg border-2 transition-all ${
                              isSelected 
                                ? 'border-custom-orange bg-orange-50' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center flex-1">
                              <div className="relative flex items-center">
                                <input
                                  type="radio"
                                  name="global-border-option"
                                  value={boId}
                                  checked={isSelected}
                                  onChange={(e) => setGlobalBorderOption(e.target.value)}
                                  className="sr-only"
                                />
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? 'border-custom-orange bg-custom-orange' 
                                    : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 rounded-full bg-white"></div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-3">
                                <span className={`text-sm ${isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {boName}
                                </span>
                                {boDescription && (
                                  <p className="text-xs text-gray-500 mt-0.5">{boDescription}</p>
                                )}
                              </div>
                            </div>
                            {(boPrice > 0 || (borderOption.percentage && borderOption.percentage !== 0)) && (
                              <span className={`text-sm font-semibold ml-3 ${isSelected ? 'text-custom-orange' : 'text-gray-600'}`}>
                                {borderOption.price_type === 'percentage' ? 
                                  `${borderOption.percentage > 0 ? '+' : ''}${borderOption.percentage}%` :
                                  `${boPrice > 0 ? '+' : ''}$${Math.abs(boPrice / 100).toFixed(2)}`
                                }
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Pricing Summary */}
          {cartTotal ? (
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal ({cartTotal.total_quantity} items)</span>
                <span className="font-medium">${(cartTotal.subtotal / 100).toFixed(2)}</span>
              </div>
              
              {cartTotal.discount_amount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{cartTotal.discount_description}</span>
                  <span>-${(cartTotal.discount_amount / 100).toFixed(2)}</span>
                </div>
              )}
              
              {appliedCoupon && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon ({couponCode})</span>
                  <span>-${(calculateDiscount() / 100).toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">
                  {selectedShipping ? (
                    `$${((shippingOptions[selectedShipping]?.cost || 0) / 100).toFixed(2)}`
                  ) : (
                    <span className="text-red-500 text-sm">Please select</span>
                  )}
                </span>
              </div>
              
              {cartTotal.tax_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {cartTotal.tax_name || 'Tax'} ({((cartTotal.tax_rate || 0.10) * 100).toFixed(0)}%)
                    {cartTotal.tax_included_in_shipping && selectedShipping && ' (incl. shipping)'}
                  </span>
                  <span className="font-medium">
                    ${(() => {
                      const couponDiscount = calculateDiscount();
                      let displayTax = cartTotal.tax_amount;
                      const shipping = shippingOptions[selectedShipping]?.cost || 0;
                      
                      // Recalculate tax if "apply to coupons" is enabled and there's a coupon
                      if (cartTotal.tax_apply_to_coupons && couponDiscount > 0 && cartTotal.tax_included_in_prices) {
                        // Tax is included in prices, so we need to recalculate tax based on discounted amount
                        const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
                        displayTax = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate / (1 + cartTotal.tax_rate));
                      }
                      
                      // Add tax on shipping if enabled
                      if (cartTotal.tax_included_in_shipping && shipping > 0) {
                        const shippingTax = Math.floor(shipping * cartTotal.tax_rate);
                        displayTax += shippingTax;
                      }
                      
                      return (displayTax / 100).toFixed(2);
                    })()}
                  </span>
                </div>
              )}
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span>
                  ${(() => {
                    const couponDiscount = calculateDiscount();
                    const subtotalAfterDiscount = cartTotal.subtotal_after_discount - couponDiscount;
                    const shipping = shippingOptions[selectedShipping]?.cost || 0;
                    
                    if (cartTotal.tax_included_in_prices) {
                      // Tax is already included in prices AND shipping
                      // Just add them together, don't add tax again
                      return ((subtotalAfterDiscount + shipping) / 100).toFixed(2);
                    } else {
                      // Tax NOT included in prices - need to add it
                      let taxAmount = cartTotal.tax_amount;
                      if (cartTotal.tax_apply_to_coupons && couponDiscount > 0) {
                        // Recalculate tax on the discounted amount
                        const subtotalAfterCoupon = cartTotal.subtotal_after_discount - couponDiscount;
                        taxAmount = Math.floor(subtotalAfterCoupon * cartTotal.tax_rate);
                      }
                      
                      // Add tax to shipping if tax_included_in_shipping is enabled
                      if (cartTotal.tax_included_in_shipping && shipping > 0) {
                        const shippingTax = Math.floor(shipping * cartTotal.tax_rate);
                        taxAmount += shippingTax;
                      }
                      
                      return ((subtotalAfterDiscount + taxAmount + shipping) / 100).toFixed(2);
                    }
                  })()}
                </span>
              </div>
              
              {/* Minimum Order Validation */}
              {cartTotal && minimumOrderAmount > 0 && (cartTotal.subtotal / 100) < minimumOrderAmount && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700 font-medium">
                    ⚠️ Minimum order amount: ${minimumOrderAmount.toFixed(2)} AUD
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Your current subtotal is ${(cartTotal.subtotal / 100).toFixed(2)} AUD. Please add ${(minimumOrderAmount - (cartTotal.subtotal / 100)).toFixed(2)} AUD more to proceed.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-sm mb-6">Calculating totals...</div>
          )}
          
          {/* Payment Button */}
          {!showPayment ? (
            <Button 
              onClick={() => {
                if (!selectedShipping) {
                  toast.error('Please select a shipping method');
                  return;
                }
                // Check minimum order amount
                if (minimumOrderAmount > 0 && cartTotal && (cartTotal.subtotal / 100) < minimumOrderAmount) {
                  toast.error(`Minimum order amount is $${minimumOrderAmount.toFixed(2)} AUD`);
                  return;
                }
                setShowPaymentModal(true);
              }}
              disabled={
                loading || 
                !cartTotal || 
                !selectedShipping || 
                (minimumOrderAmount > 0 && cartTotal && (cartTotal.subtotal / 100) < minimumOrderAmount)
              }
              variant="outline"
              className="w-full text-custom-orange hover:text-orange-700 hover:bg-orange-50 h-12"
            >
              {loading ? (
                <>
                  <i className="pi pi-spin pi-spinner mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="pi pi-credit-card mr-2"></i>
                  Select Payment Method
                </>
              )}
            </Button>
          ) : (
            <PaymentSection 
              order={currentOrder}
              cartTotal={cartTotal}
              onPaymentComplete={handlePaymentComplete}
              onCancel={() => setShowPayment(false)}
              setActiveTab={setActiveTab}
              stripePublicKey={stripePublicKey}
            />
          )}
          
          <Button 
            variant="outline"
            onClick={() => setActiveTab('cart')}
            className="w-full mt-3"
          >
            ← Back to Cart
          </Button>
        </Card>
      </div>

      {/* Payment Method Selection Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('dialog.select_payment', 'Select Payment Method')}</DialogTitle>
            <DialogDescription>
              {t('dialog.select_payment_desc', 'Choose how you would like to pay')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {paymentProviders.includes('stripe') && (
              <button
                onClick={() => handleSelectPaymentMethod('stripe')}
                className="animated-border-element w-full p-4 border-2 border-gray-200 rounded-lg hover:bg-blue-50 transition-colors text-left outline-none"
              >
                <div className="flex items-center">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Stripe</h4>
                    <p className="text-sm text-gray-600">Pay securely with credit card</p>
                  </div>
                  <svg className="w-16 h-10" viewBox="0 0 60 25" fill="none">
                    <path fill="#635BFF" d="M0 12.5C0 5.596 5.596 0 12.5 0h35C54.404 0 60 5.596 60 12.5S54.404 25 47.5 25h-35C5.596 25 0 19.404 0 12.5z"/>
                    <path fill="#FFF" d="M29.065 9.548c0-.893.74-1.234 1.963-1.234 1.765 0 3.991.535 5.754 1.49V5.835c-1.93-.787-3.84-1.101-5.754-1.101-4.704 0-7.825 2.458-7.825 6.558 0 6.396 8.79 5.368 8.79 8.131 0 1.07-.93 1.425-2.233 1.425-1.93 0-4.413-.787-6.377-1.85v3.99c2.15.892 4.322 1.275 6.377 1.275 4.81 0 8.132-2.375 8.132-6.537 0-6.898-8.878-5.69-8.878-8.177z"/>
                  </svg>
                </div>
              </button>
            )}
            
            {paymentProviders.includes('square') && (
              <button
                onClick={() => handleSelectPaymentMethod('square')}
                className="animated-border-element w-full p-4 border-2 border-gray-200 rounded-lg hover:bg-green-50 transition-colors text-left outline-none"
              >
                <div className="flex items-center">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Square</h4>
                    <p className="text-sm text-gray-600">Pay securely with Square</p>
                  </div>
                  <svg className="w-16 h-10" viewBox="0 0 60 25" fill="none">
                    <rect width="60" height="25" rx="4" fill="#3E4348"/>
                    <rect x="10" y="7" width="11" height="11" rx="2" fill="white"/>
                    <rect x="39" y="7" width="11" height="11" rx="2" fill="white"/>
                  </svg>
                </div>
              </button>
            )}
            
            {testModeEnabled && (
              <button
                onClick={() => handleSelectPaymentMethod('test_mode')}
                className="animated-border-element w-full p-4 border-2 border-gray-200 rounded-lg hover:bg-orange-50 transition-colors text-left outline-none"
              >
                <div className="flex items-center">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Test Payment (Skip Payment)</h4>
                    <p className="text-sm text-gray-600">For testing only - Order marked as paid</p>
                  </div>
                  <div className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded">
                    TEST MODE
                  </div>
                </div>
              </button>
            )}
            
            {!paymentProviders.length && !testModeEnabled && (
              <div className="text-center py-8 text-gray-500">
                <p>No payment methods configured</p>
                <p className="text-sm mt-2">Please contact the administrator</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Payment Section Component (reused from App.js)
function PaymentSection({ order, cartTotal, onPaymentComplete, onCancel, setActiveTab, stripePublicKey }) {
  const [loading, setLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);

  // Load and check Stripe
  useEffect(() => {
    const loadStripe = () => {
      // If Stripe is already loaded
      if (window.Stripe) {
        setStripeReady(true);
        return;
      }

      // Check if script tag exists
      const existingScript = document.querySelector('script[src*="stripe.com"]');
      
      if (!existingScript) {
        // Dynamically load Stripe.js if not present
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => {
          setStripeReady(true);
        };
        script.onerror = () => {
          console.error('Failed to load Stripe.js');
          toast.error('Failed to load payment system. Please check your internet connection.');
        };
        document.head.appendChild(script);
      } else {
        // Script exists, wait for it to load
        const checkStripe = setInterval(() => {
          if (window.Stripe) {
            setStripeReady(true);
            clearInterval(checkStripe);
          }
        }, 100);
        
        // Stop checking after 10 seconds
        setTimeout(() => clearInterval(checkStripe), 10000);
      }
    };

    loadStripe();
  }, []);

  const handleStripePayment = async () => {
    setLoading(true);
    try {
      const subtotalAfterDiscount = cartTotal.subtotal_after_discount;
      const totalAmount = cartTotal.tax_included_in_prices
        ? subtotalAfterDiscount
        : subtotalAfterDiscount + cartTotal.tax_amount;
      
      if (!stripePublicKey) {
        toast.error('Stripe is not configured. Please contact the administrator.');
        console.error('Stripe public key is missing');
        setLoading(false);
        return;
      }
      
      // Wait for Stripe.js to load (max 5 seconds)
      let attempts = 0;
      while (!window.Stripe && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!window.Stripe) {
        toast.error('Stripe payment system failed to load. Please refresh the page and try again.');
        console.error('Stripe.js not loaded after 5 seconds');
        setLoading(false);
        return;
      }
      
      const stripe = window.Stripe(stripePublicKey);
      
      const response = await axios.post('/payments/create-checkout-session', {
        order_id: order.order_id,
        amount: totalAmount,
      });

      const result = await stripe.redirectToCheckout({
        sessionId: response.data.session_id,
      });

      if (result.error) {
        toast.error(result.error.message);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-4">
      <h3 className="text-lg font-semibold mb-4">Complete Payment</h3>
      {!stripeReady && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
          ⏳ Loading Stripe payment system...
        </div>
      )}
      <div className="space-y-4">
        <Button 
          variant="outline"
          onClick={handleStripePayment}
          disabled={loading || !stripeReady}
          className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          {loading ? 'Redirecting to Stripe...' : 
           !stripeReady ? 'Loading Stripe...' : 
           'Pay with Stripe'}
        </Button>
        <Button 
          onClick={onCancel}
          variant="outline"
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export default CheckoutSection;
